import React, { FC } from 'react';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { useFormat } from '../../../hooks/index';
import {
  DetailsTab,
  getConversionMethodTitle,
  Safe,
  safeCapTypeTitle,
} from '../../../types/safes.types';
import { toNumber } from '../../../utils/to-number.util';

export type SafeItemProps = {
  safe: Safe;
  selectedTab: DetailsTab;
};

export const SafeItem: FC<SafeItemProps> = ({ safe, selectedTab }) => {
  const {
    stakeholder,
    investment,
    conversionDate,
    conversionMethod,
    convertedShares,
    floorValue,
    issueDate,
    maturityDate,
    discount,
    capValue,
    capType,
  } = safe;

  const { format } = useFormat();

  return (
    <div className="flex min-w-[305px] flex-col divide-y-[1px] divide-gray-100 rounded-md shadow-sm">
      <div className="flex w-full items-center gap-3 p-4 pl-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-[450] text-gray-700">
          {stakeholder.fullName[0]}
        </div>
        <span className="w-full truncate text-nowrap text-sm font-[550] text-gray-700">
          {stakeholder.fullName}
        </span>
      </div>
      <div className="flex w-full items-center justify-between p-3 pr-4">
        <span className="text-nowrap text-sm font-[450] text-gray-700">
          <AppFormattedMessage id="INVESTMENT" />
        </span>
        <span className="text-nowrap text-sm font-[450] text-gray-700">
          {toNumber(investment)?.toLocaleString('en-US')}
        </span>
      </div>
      <div className="flex w-full items-center justify-between p-3 pr-4">
        <span className="text-nowrap text-sm font-[450] text-gray-700">
          <AppFormattedMessage id="ISSUE_DATE" />
        </span>
        <span className="text-nowrap text-sm font-[450] text-gray-700">
          {issueDate ? format(issueDate, 'dd/MM/yyyy') : '-'}
        </span>
      </div>

      {selectedTab === DetailsTab.PENDING_CONVERSION ? (
        <>
          <div className="flex w-full items-center justify-between p-3 pr-4">
            <span className="text-nowrap text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="VAL_CAP" />
            </span>
            <span className="text-nowrap text-sm font-[450] text-gray-700">
              {capValue ? toNumber(capValue)?.toLocaleString('en-US') : '-'}
            </span>
          </div>
          <div className="flex w-full items-center justify-between p-3 pr-4">
            <span className="text-nowrap text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="CAP_TYPE" />
            </span>
            <span className="text-nowrap text-sm font-[450] text-gray-700">
              {capValue && capType ? <AppFormattedMessage id={safeCapTypeTitle[capType]} /> : '-'}
            </span>
          </div>
          <div className="flex w-full items-center justify-between p-3 pr-4">
            <span className="text-nowrap text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="SHARE_PRICE_DISCOUNT_PERCENTAGE" />
            </span>
            <span className="text-nowrap text-sm font-[450] text-gray-700">
              {discount ? `${discount * 100}%` : '-'}
            </span>
          </div>
          <div className="flex w-full items-center justify-between p-3 pr-4">
            <span className="text-nowrap text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="FLOOR" />
            </span>
            <span className="text-nowrap text-sm font-[450] text-gray-700">
              {floorValue ? toNumber(floorValue)?.toLocaleString('en-US') : '-'}
            </span>
          </div>
          <div className="flex w-full items-center justify-between p-3 pr-4">
            <span className="text-nowrap text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="MATURITY_DATE" />
            </span>
            <span className="text-nowrap text-sm font-[450] text-gray-700">
              {maturityDate ? format(maturityDate, 'dd/MM/yyyy') : '-'}
            </span>
          </div>
        </>
      ) : (
        <>
          <div className="flex w-full items-center justify-between p-3 pr-4">
            <span className="text-nowrap text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="CONVERSION_METHOD" />
            </span>
            <span className="text-nowrap text-sm font-[450] text-gray-700">
              {conversionMethod ? (
                <AppFormattedMessage id={getConversionMethodTitle(conversionMethod, capType)} />
              ) : (
                '-'
              )}
            </span>
          </div>
          <div className="flex w-full items-center justify-between p-3 pr-4">
            <span className="text-nowrap text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="CONVERSION_DATE" />
            </span>
            <span className="text-nowrap text-sm font-[450] text-gray-700">
              {conversionDate ? format(conversionDate, 'dd/MM/yyyy') : '-'}
            </span>
          </div>
          <div className="flex w-full items-center justify-between p-3 pr-4">
            <span className="text-nowrap text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="SHARES" />
            </span>
            <span className="text-nowrap text-sm font-[450] text-gray-700">
              {convertedShares ? toNumber(convertedShares)?.toLocaleString('en-US') : '-'}
            </span>
          </div>
        </>
      )}
    </div>
  );
};
